import React, { useState, useRef, useEffect } from 'react';
import { MdChat, MdClose, MdSend } from 'react-icons/md';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(() => {
    const saved = localStorage.getItem('chatbot_history');
    if (saved) {
      const parsed = JSON.parse(saved) as Array<{
        id: number;
        text: string;
        sender: 'user' | 'bot';
        timestamp: string;
      }>;
      return parsed.map((msg) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
    return [
      {
        id: 1,
        text: "Hi! I'm Balaji's assistant. Ask me anything about his skills, projects, or experience!",
        sender: 'bot' as const,
        timestamp: new Date(),
      },
    ];
  });
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem('chatbot_width');
    return saved ? parseInt(saved) : 384;
  });
  const [height, setHeight] = useState(() => {
    const saved = localStorage.getItem('chatbot_height');
    return saved ? parseInt(saved) : 384;
  });
  const [isResizing, setIsResizing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Escape HTML entities to prevent XSS
  const escapeHtml = (text: string): string => {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
  };

  // Convert markdown to HTML (with XSS protection)
  const markdownToHtml = (text: string) => {
    // Escape HTML first to prevent XSS attacks
    let html = escapeHtml(text);

    // Bold text: **text** -> <strong>text</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic text: *text* -> <em>text</em>
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Line breaks
    html = html.replace(/\n/g, '<br />');

    // Bullet points: - text or * text -> <li>text</li>
    const bulletPattern = /^[\s]*([-*])\s+(.+)$/gm;
    const bulletMatches = [...html.matchAll(bulletPattern)];

    if (bulletMatches.length > 0) {
      let lastIndex = 0;
      let result = '';

      bulletMatches.forEach((match, index) => {
        const fullText = match[0];
        const startIndex = match.index!;

        // Add text before this bullet
        result += html.substring(lastIndex, startIndex);

        // Check if this is the start of a list
        const isStartOfList = index === 0 || bulletMatches[index - 1].index! + bulletMatches[index - 1][0].length < startIndex - 5;

        if (isStartOfList) {
          result += '<ul class="list-disc list-inside space-y-1">';
        }

        result += `<li>${match[2]}</li>`;

        // Check if this is the end of a list
        const isEndOfList = index === bulletMatches.length - 1 || bulletMatches[index + 1].index! > startIndex + fullText.length + 5;

        if (isEndOfList) {
          result += '</ul>';
        }

        lastIndex = startIndex + fullText.length;
      });

      html = result + html.substring(lastIndex);
    }

    return html;
  };

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('chatbot_history', JSON.stringify(messages));
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Save dimensions to localStorage
  useEffect(() => {
    localStorage.setItem('chatbot_width', width.toString());
    localStorage.setItem('chatbot_height', height.toString());
  }, [width, height]);

  // Handle resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!widgetRef.current) return;

      const rect = widgetRef.current.getBoundingClientRect();
      const newWidth = Math.max(280, e.clientX - rect.left + 10);
      const newHeight = Math.max(300, e.clientY - rect.top + 10);

      setWidth(newWidth);
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    // Validate message length
    if (trimmedInput.length > 1000) {
      setError('Message is too long (max 1000 characters)');
      return;
    }

    setError(null);

    // Add user message
    const userMessage: Message = {
      id: messages.length + 1,
      text: trimmedInput,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Send full conversation history including the new user message
      const allMessages = [
        ...messages,
        userMessage,
      ].map((msg) => ({
        role: msg.sender === 'user' ? ('user' as const) : ('assistant' as const),
        content: msg.text,
      }));

      // Call Netlify Function or backend API
      const apiUrl = process.env.REACT_APP_CHATBOT_API_URL || '/.netlify/functions/chat';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: allMessages,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `API error: ${response.status}`);
      }

      const data = await response.json();

      // Add bot response
      const botMessage: Message = {
        id: messages.length + 2,
        text: data.response,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to get response from AI assistant';

      console.error('ChatBot Error:', err);

      // Add error message
      const errorMsg: Message = {
        id: messages.length + 2,
        text: `Sorry, I encountered an error: ${errorMessage}. Please try again or contact Balaji directly at balajiiyer29@gmail.com.`,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMsg]);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        text: "Hi! I'm Balaji's assistant. Ask me anything about his skills, projects, or experience!",
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
    setError(null);
  };

  return (
    <>
      {/* Chat Widget */}
      <div
        ref={widgetRef}
        className={`fixed bottom-5 right-5 z-50 rounded-lg bg-white shadow-2xl flex flex-col transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        } ${isResizing ? 'select-none' : ''}`}
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-4 rounded-t-lg flex justify-between items-center">
          <h3 className="text-lg font-semibold">Balaji's Assistant</h3>
          <div className="flex gap-2">
            <button
              onClick={clearChat}
              title="Clear chat"
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm transition"
            >
              ↻
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="bg-white/20 hover:bg-white/30 p-2 rounded transition"
            >
              <MdClose size={18} />
            </button>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4 space-y-3">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} animate-slideIn`}
            >
              <div className="flex flex-col gap-1 max-w-xs">
                <div
                  className={`px-4 py-2 rounded-lg break-words ${
                    msg.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-white border border-gray-200 text-gray-900 rounded-bl-none'
                  }`}
                >
                  {msg.sender === 'bot' ? (
                    <div
                      className="prose prose-sm max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.text) }}
                    />
                  ) : (
                    msg.text
                  )}
                </div>
                <span className="text-xs text-gray-500 px-2">
                  {msg.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 rounded-bl-none">
                <span className="flex gap-1">
                  <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                  <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                </span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="text-xs text-red-600 px-2 py-1 bg-red-50 rounded">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Form */}
        <form
          onSubmit={handleSendMessage}
          className="border-t border-gray-200 bg-white px-4 py-3 flex gap-2"
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask me something..."
            disabled={isLoading}
            maxLength={1000}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Send message"
          >
            <MdSend size={18} />
          </button>
        </form>

        {/* Resize Handle */}
        <div
          onMouseDown={handleMouseDown}
          className={`absolute bottom-0 right-0 w-6 h-6 cursor-se-resize bg-gradient-to-tl from-blue-600 to-transparent rounded-tl-lg opacity-0 hover:opacity-100 transition-opacity ${
            isResizing ? 'opacity-100' : ''
          }`}
          title="Drag to resize"
        />
      </div>

      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          title="Open chat"
          className="fixed bottom-5 right-5 z-50 w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        >
          <MdChat size={28} />
        </button>
      )}
    </>
  );
};

export default ChatBot;
